import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Wat is een NDA (geheimhoudingsovereenkomst)?',
    answer: 'Een NDA (Non-Disclosure Agreement), ook wel geheimhoudingsovereenkomst of vertrouwelijkheidsovereenkomst genoemd, is een juridisch contract waarbij partijen afspreken om vertrouwelijke informatie niet te delen met derden. Als freelancer kom je regelmatig in aanraking met gevoelige bedrijfsinformatie, klantgegevens of innovatieve ideeën van je opdrachtgever.',
  },
  {
    question: 'Wanneer heb je een NDA nodig?',
    answer: 'Je hebt een NDA nodig wanneer je als freelancer toegang krijgt tot vertrouwelijke informatie van een opdrachtgever, zoals bedrijfsstrategieën, klantgegevens, broncode, financiële gegevens of productideeën. Veel grote bedrijven eisen een ondertekende NDA voordat ze met een freelancer samenwerken.',
  },
  {
    question: 'Hoe lang is een NDA geldig?',
    answer: 'De geldigheid van een NDA wordt bepaald door de partijen zelf en staat vermeld in het contract. Typisch varieert de duur van 1 tot 5 jaar na beëindiging van de samenwerking. Sommige NDA\'s zijn zelfs onbeperkt geldig voor bepaalde categorieën informatie, zoals bedrijfsgeheimen.',
  },
  {
    question: 'Wat moet er in een NDA staan?',
    answer: 'Een goede NDA bevat: een duidelijke definitie van wat als vertrouwelijke informatie wordt beschouwd, de verplichtingen van de ontvangende partij, uitzonderingen op de geheimhoudingsplicht, de duur van de geheimhouding, de gevolgen van schending (boeteclausule), en het toepasselijk recht en de bevoegde rechtbank.',
  },
  {
    question: 'Is een NDA juridisch afdwingbaar?',
    answer: 'Ja, een correct opgestelde en ondertekende NDA is juridisch afdwingbaar in zowel België als Nederland. Bij schending kan de benadeelde partij schadevergoeding eisen en een rechterlijk verbod aanvragen. Het is belangrijk dat de NDA duidelijk en specifiek is opgesteld om afdwingbaar te zijn.',
  },
];

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/nda-template',
      url: 'https://pactorae.com/nda-template',
      name: 'NDA Template voor Freelancers (Geheimhoudingsovereenkomst) | Pactorae',
      description:
        'Gratis NDA template voor freelancers. Professionele geheimhoudingsovereenkomst voor België en Nederland. Bescherm vertrouwelijke informatie van jouw klanten.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-[#2F4E6F] pr-4">{faq?.question}</span>
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
          style={{ backgroundColor: '#FF8C00', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
        >
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{faq?.answer}</p>
        </div>
      )}
    </div>
  );
};

const NdaPreview = () => (
  <div className="relative">
    <div
      className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      style={{ maxWidth: '720px', margin: '0 auto' }}
    >
      <div style={{ padding: '40px 48px 0 48px', fontFamily: 'Georgia, serif', color: '#1a1a1a', lineHeight: '1.8', fontSize: '14px' }}>
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16px', letterSpacing: '0.08em', marginBottom: '4px', textTransform: 'uppercase' }}>GEHEIMHOUDINGSOVEREENKOMST (NDA)</p>
        <p style={{ textAlign: 'center', color: '#555', fontSize: '13px', marginBottom: '32px' }}>Opgesteld via Pactorae — pactorae.com</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>PARTIJEN</p>
        <p style={{ marginBottom: '4px' }}><strong>Ontvanger:</strong> Thomas Janssen, Mechelsesteenweg 88, 2018 Antwerpen, BTW BE0456.789.123</p>
        <p style={{ marginBottom: '24px' }}><strong>Verstrekker:</strong> Innovatech NV, Leuvenseweg 210, 1000 Brussel, BTW BE0321.654.987</p>

        <p style={{ marginBottom: '16px' }}>Hierna gezamenlijk aangeduid als "de Partijen", komen het volgende overeen:</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 1 — DEFINITIES EN VERTROUWELIJKE INFORMATIE</p>
        <p style={{ marginBottom: '8px' }}>Onder "Vertrouwelijke Informatie" wordt in het kader van deze overeenkomst verstaan alle informatie die door de Verstrekker aan de Ontvanger wordt meegedeeld, inclusief maar niet beperkt tot:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Bedrijfsstrategieën, businessplannen en marktanalyses</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Klantgegevens, leverancierslijsten en contracten</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Financiële informatie, budgetten en prognoses</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Broncode, algoritmen en technische specificaties</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• Productideeën, prototypes en innovatieplannen</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 2 — GEHEIMHOUDINGSPLICHT</p>
        <p style={{ marginBottom: '16px' }}>De Ontvanger verbindt zich ertoe de Vertrouwelijke Informatie strikt geheim te houden en niet te delen met derden zonder voorafgaande schriftelijke toestemming van de Verstrekker. De Ontvanger zal de Vertrouwelijke Informatie uitsluitend gebruiken voor het uitvoeren van de opdracht zoals omschreven in de samenwerkingsovereenkomst.</p>
        <p style={{ marginBottom: '24px' }}>De Ontvanger zal passende technische en organisatorische maatregelen nemen om de Vertrouwelijke Informatie te beschermen tegen ongeoorloofde toegang, verlies of vernietiging.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 3 — UITZONDERINGEN</p>
        <p style={{ marginBottom: '8px' }}>De geheimhoudingsplicht is niet van toepassing op informatie die:</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Reeds openbaar bekend was op het moment van mededeling</p>
        <p style={{ marginBottom: '4px', paddingLeft: '16px' }}>• Onafhankelijk door de Ontvanger is ontwikkeld</p>
        <p style={{ marginBottom: '24px', paddingLeft: '16px' }}>• Op grond van een wettelijke verplichting moet worden vrijgegeven</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 4 — BOETECLAUSULE</p>
        <p style={{ marginBottom: '16px' }}>Bij schending van de geheimhoudingsplicht is de Ontvanger een onmiddellijk opeisbare boete verschuldigd van <strong>€ 10.000,00 (tienduizend euro)</strong> per overtreding, onverminderd het recht van de Verstrekker om aanvullende schadevergoeding te vorderen.</p>

        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>ARTIKEL 5 — DUUR</p>
        <p style={{ marginBottom: '16px' }}>Deze geheimhoudingsovereenkomst treedt in werking op de datum van ondertekening en blijft van kracht gedurende <strong>3 jaar</strong> na beëindiging van de samenwerking tussen Partijen. Voor bedrijfsgeheimen en bijzonder gevoelige informatie geldt een onbeperkte geheimhoudingsplicht.</p>
      </div>

      {/* Fade-out gradient overlay */}
      <div
        style={{
          height: '160px',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,1) 100%)',
          marginTop: '-80px',
          position: 'relative',
          zIndex: 1,
        }}
      />

      {/* Below fade message */}
      <div style={{ padding: '0 48px 32px 48px', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', marginBottom: '20px' }}>
          Volledig contract genereren met de Pactorae generator
        </p>
        <Link
          to="/contract-generator"
          className="inline-block font-semibold px-8 py-3 rounded-lg text-white transition-colors duration-200 shadow-md hover:shadow-lg"
          style={{ backgroundColor: '#FF8C00' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e07b00'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF8C00'}
        >
          Genereer jouw contract gratis
        </Link>
      </div>
    </div>
  </div>
);

const NdaTemplatePage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>NDA Template voor Freelancers België &amp; Nederland | Pactorae</title>
        <meta
          name="description"
          content="Gratis NDA template voor freelancers en zelfstandigen in België en Nederland. Professionele geheimhoudingsovereenkomst voor zzp'ers en zelfstandigen. Bescherm vertrouwelijke informatie. Genereer direct online."
        />
        <meta
          name="keywords"
          content="nda template België, nda template Nederland, geheimhoudingsovereenkomst freelancer, nda zzp, non disclosure agreement België, non disclosure agreement Nederland, geheimhouding overeenkomst"
        />
        <link rel="canonical" href="https://pactorae.com/nda-template" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/nda-template" />
        <meta property="og:title" content="NDA Template voor Freelancers België &amp; Nederland | Pactorae" />
        <meta
          property="og:description"
          content="Gratis NDA template voor freelancers en zzp'ers in België en Nederland. Professionele geheimhoudingsovereenkomst."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NDA Template voor Freelancers België &amp; Nederland | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis NDA template voor freelancers en zzp'ers in België en Nederland. Bescherm vertrouwelijke informatie."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#FF8C00]/20 text-[#FF8C00] text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-[#FF8C00]/30">
              Gratis Template
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              NDA Template voor Freelancers
              <span className="block text-[#FF8C00] mt-2">België &amp; Nederland</span>
            </h1>
            <p className="text-xl text-blue-100 mb-4 max-w-2xl mx-auto">
              Bescherm vertrouwelijke informatie van jouw opdrachtgevers met een professionele NDA. Gratis template voor freelancers en zzp'ers in België en Nederland — juridisch correct en direct te gebruiken.
            </p>
            <p className="text-sm text-blue-200 mb-8 max-w-xl mx-auto">
              Geschikt voor zelfstandigen in België, zzp'ers in Nederland en alle freelance professionals.
            </p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wat is een NDA voor freelancers in België en Nederland?</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Een NDA (Non-Disclosure Agreement) of geheimhoudingsovereenkomst is een essentieel document voor elke freelancer en zzp'er. Of je nu als zelfstandige in België of als zzp'er in Nederland werkt — een NDA beschermt de vertrouwelijke informatie van jouw opdrachtgever en legt juridisch vast wat je wel en niet mag delen. Onze generator maakt NDA's op maat voor zowel het Belgisch als het Nederlands recht.
            </p>
          </section>

          {/* What's included */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Wat bevat dit NDA template?</h2>
            <ul className="space-y-3">
              {[
                { title: 'Definitie vertrouwelijke informatie', desc: 'Exacte omschrijving van wat als vertrouwelijk wordt beschouwd' },
                { title: 'Geheimhoudingsplicht', desc: 'Verplichtingen van de ontvanger om informatie niet te delen' },
                { title: 'Uitzonderingen', desc: 'Situaties waarbij de geheimhoudingsplicht niet van toepassing is' },
                { title: 'Duur van de NDA', desc: 'Hoe lang de geheimhoudingsplicht van kracht blijft' },
                { title: 'Boeteclausule', desc: 'Financiële gevolgen bij schending van de overeenkomst' },
                { title: 'Teruggave van informatie', desc: 'Verplichting om documenten terug te geven na afloop' },
                { title: 'Toepasselijk recht', desc: 'Belgisch of Nederlands recht en bevoegde rechtbank' },
              ]?.map((item, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <span className="flex-shrink-0 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <span className="font-semibold text-[#2F4E6F]">{item?.title}:</span>{' '}
                    <span className="text-gray-600">{item?.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* NDA Preview */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Voorbeeld NDA (geheimhoudingsovereenkomst)</h2>
            <NdaPreview />
          </section>

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-xl font-bold text-[#2F4E6F] mb-4">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { to: '/freelance-contract-template', label: 'Freelance Contract Template' },
                { to: '/consultancy-contract-template', label: 'Consultancy Contract Template' },
                { to: '/algemene-voorwaarden-generator', label: 'Algemene Voorwaarden Generator' },
                { to: '/freelance-contract-voorbeeld', label: 'Freelance Contract Voorbeeld' },
              ]?.map((link) => (
                <Link
                  key={link?.to}
                  to={link?.to}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-colors text-[#2F4E6F] font-medium text-sm"
                >
                  <span style={{ color: '#FF8C00' }}>→</span> {link?.label}
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-8">Veelgestelde vragen over NDA's</h2>
            <div className="space-y-3">
              {faqs?.map((faq, i) => (
                <FaqItem key={i} faq={faq} />
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-gradient-to-br from-[#2F4E6F] to-[#1e3550] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-white mb-4">Klaar om jouw NDA te genereren?</h2>
            <p className="text-blue-100 mb-8">Maak in 30 seconden een professionele geheimhoudingsovereenkomst op maat.</p>
            <Link
              to="/contract-generator"
              className="inline-block bg-[#FF8C00] hover:bg-[#e07b00] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors duration-200 shadow-lg"
            >
              Genereer jouw contract gratis
            </Link>
          </section>
        </article>
      </main>
    </>
  );
};

export default NdaTemplatePage;
